import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, money } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, ShieldCheck, ArrowRight, Banknote } from "lucide-react";

export default function Approvals() {
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);

  const load = () => {
    api.get("/payroll?status=pending_approval").then((r) => setPending(r.data)).catch(() => {});
    api.get("/payroll?status=approved").then((r) => setApproved(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const approve = (p) => act(() => api.post(`/payroll/${p.id}/verify`, { decision: "approved", note: "" }), "Approved");
  const reject = (p) => {
    const note = window.prompt("Reason for rejection?") || "";
    act(() => api.post(`/payroll/${p.id}/verify`, { decision: "rejected", note }), "Rejected");
  };
  const process = (p) => act(() => api.post(`/payroll/${p.id}/process`), "Processed & disbursed");

  const Row = ({ p, actions }) => (
    <div data-testid={`approval-row-${p.id}`} className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 hover:bg-slate-50 transition-colors">
      <div className="min-w-0">
        <div className="flex items-center gap-2.5">
          <p className="font-semibold text-slate-800">{p.company_name}</p>
          <StatusBadge status={p.status} />
        </div>
        <p className="text-xs text-slate-500 mt-0.5">Week of {new Date(p.week_start).toLocaleDateString()} · {p.totals.employees} employees · submitted by {p.submitted_by_name || "—"}</p>
      </div>
      <div className="flex items-center gap-4">
        <span className="font-mono font-bold text-sky-600">{money(p.totals.net)}</span>
        <Link to={`/app/payroll/${p.id}`} className="text-sm font-semibold text-slate-500 hover:text-sky-600 inline-flex items-center gap-1" data-testid={`review-${p.id}`}>Review <ArrowRight className="w-4 h-4" /></Link>
        {actions}
      </div>
    </div>
  );

  return (
    <div className="animate-fade-up">
      <div className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-wider text-sky-600">Back-End</p>
        <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">Verification Queue</h1>
        <p className="text-slate-500 mt-1">Review submitted payroll, verify, then process disbursement.</p>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden mb-8">
        <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-100">
          <ShieldCheck className="w-5 h-5 text-amber-500" />
          <h2 className="font-display font-bold text-lg">Pending approval</h2>
          <span className="ml-auto text-xs font-semibold text-slate-400">{pending.length} awaiting</span>
        </div>
        <div className="divide-y divide-slate-100">
          {pending.length === 0 && <p className="px-6 py-10 text-center text-slate-400 text-sm">Nothing awaiting approval.</p>}
          {pending.map((p) => (
            <Row key={p.id} p={p} actions={
              <>
                <Button size="sm" onClick={() => approve(p)} data-testid={`approve-${p.id}`} className="bg-emerald-600 hover:bg-emerald-700 rounded-full"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                <Button size="sm" variant="outline" onClick={() => reject(p)} data-testid={`reject-${p.id}`} className="rounded-full border-slate-300 text-rose-600 hover:bg-rose-50"><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
              </>
            } />
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-100">
          <Banknote className="w-5 h-5 text-sky-500" />
          <h2 className="font-display font-bold text-lg">Approved — ready to disburse</h2>
        </div>
        <div className="divide-y divide-slate-100">
          {approved.length === 0 && <p className="px-6 py-10 text-center text-slate-400 text-sm">No approved runs waiting.</p>}
          {approved.map((p) => (
            <Row key={p.id} p={p} actions={
              <Button size="sm" onClick={() => process(p)} data-testid={`process-${p.id}`} className="bg-sky-600 hover:bg-sky-700 rounded-full"><Banknote className="w-3.5 h-3.5 mr-1" /> Process</Button>
            } />
          ))}
        </div>
      </div>
    </div>
  );
}
